<template>
<div data-component="VideoTrigger.vue" @click.stop>
	<!-- Infoboks -->
	<div v-if="!hideTrigger" class="flex bg-[#FFE5B2] rounded-lg overflow-hidden" :class="mobileSize ? 'relative justify-end mx-auto max-w-sm' : ''">
		<img v-if="mobileSize" :src="thumbnailFilename" :style="{ height: '232px' }">
		<img v-else :src="thumbnailFilename" :style="{ height: '96px' }">
		<div class="flex items-center space-between grow p-4 " :class="mobileSize ? 'absolute w-min flex-col gap-6 left-0 bottom-0' : ''">
			<span class="heading-xs text-pretty grow" v-text="body"/>
			<button class="btn gap-2 h-12 px-5 text-accent bg-white whitespace-nowrap" @click.stop.prevent="openPlayer()">
				<OIcon set="video" name="play" :size="1.25"/> Obejrzyj wideo
			</button>
		</div>
	</div>

	<!-- Odtwarzacz -->
	<div ref="player">
		<div
			v-if="isPlayerVisible"
			class="fixed bottom-0 right-0 flex flex-col bg-white a-loaded z-2048"
			:class="isFullscreen ? 'top-0 left-0' : 'm-4 w-80 border border-info rounded-lg shadow-md'"
			@mousemove="resetControlsVisibility"
		>
			<div
				class="flex items-center gap-2 px-4 py-5 transition-opacity"
				:class="{
					'opacity-0': hideControls && isFullscreenEnabled,
					'absolute inset-x-0 top-0 bg-black/50 text-white backdrop:backdrop-blur z-4' : isFullscreenEnabled
				}"
			>
				<OIcon set="video" name="video-player-movie" :size="1.5" :class="isFullscreenEnabled ? 'text-current' : 'text-neutral-600'"/>
				<div class="text-base font-bold break-all line-clamp-1 grow" v-text="videos[index].title"/>
				<button class="p-2" @click="closePlayer">
					<OIcon name="close" :size="0.5"/>
				</button>
			</div>

			<div class="relative grow" :class="isFullscreen ? 'max-h-full' : 'px-2 pb-2'">
				<!-- Oceń wideo -->
				<div v-if="!videos[index].wasRated && isPaused" class="px-5 py-3 bg-[#1462D380] text-white absolute inset-x-0 z-1 font-bold text-xs flex items-center gap-5" :class="[isFullscreenEnabled && !hideControls ? 'top-16' : 'top-0', !isFullscreenEnabled && ScreenSize > 1 ? 'mx-2 rounded-t-lg' : '']">
					<div class="grow" v-text="'Czy to wideo było przydatne?'"/>
					<OIcon
						:name="rated === 1 ? 'thumbs-up-on' : 'thumbs-up'"
						:size="1.5" class="cursor-pointer"
						:class="[rated === 1 ? 'text-info-400' : 'text-white', rated ? 'pointer-events-none' : '']"
						@click="rate(1)"
					/>
					<OIcon
						:name="rated === -1 ? 'thumbs-down-on' : 'thumbs-down'"
						:size="1.5" class="cursor-pointer"
						:class="[rated === -1 ? 'text-info-400' : 'text-white', rated ? 'pointer-events-none' : '']"
						@click="rate(-1)"
					/>
				</div>

				<!-- Odtwarzacz -->
				<VideoPlayer
					:class="isFullscreen ? '!absolute inset-0 bg-neutral-900 flex items-center' : 'rounded-lg overflow-hidden'"
					:filename="videoFilename"
					:oggFilename="oggVideoFilename"
					:hideControls="hideControls && isFullscreenEnabled"
					:autoplay="autoplay"
					:displayAdditionalControls="videos.length > 1"
					@progress="onVideoProgress"
					@toggle-fullscreen="onToggleFullscreen"
					@playback-change="isPaused = $event"
					@switch-video="switchVideo($event)"
				/>

				<ToasterElement ref="toaster"/>
			</div>

			<template v-if="videos.length > 1 || canShowLink">
				<div v-if="!isFullscreenEnabled" class="flex items-center justify-center h-12 gap-2 mx-3 mb-2 font-bold text-info">
					<template v-if="canShowLink">
						<span class="grow" v-text="`${videos[index].linkLabel || 'Dowiedz się więcej'}`"/>
						<OButton color="info" variant="outlined" size="sm" label="Zobacz" @click="goToLink"/>
						<button class="p-2" @click="isLinkHidden = true">
							<OIcon name="close" :size="0.5"/>
						</button>
					</template>
					<template v-else>
						<button v-if="index" @click="switchVideo(-1)">
							<OIcon name="arrow-left" :size="1.25"/>
						</button>
						<button class="justify-end gap-2 ml-auto text-right grow" @click="switchVideo(1)">
							<div class="line-clamp line-clamp-2" v-text="`Zobacz kolejny: ${nextVideoTitle}`"/>
							<OIcon name="arrow-right" :size="1.25"/>
						</button>
					</template>
				</div>
				<div
					v-else-if="isPaused && (isVideoFinished || canShowLink)"
					class="absolute right-0 flex items-center gap-2 p-4 mx-4 mb-4 text-white rounded-lg cursor-pointer w-72 bg-black/50 bottom-16 a-loaded"
					@click="switchVideo(1)"
				>
					<template v-if="canShowLink">
						<div class="text-xs font-bold text-left line-clamp line-clamp-2 grow" v-text="`${videos[index].linkLabel || 'Dowiedz się więcej'}`"/>
						<button class="border btn btn-small border-whiter" @click.stop="goToLink" v-text="'Zobacz'"/>
						<button class="p-2" @click.stop="isLinkHidden = true">
							<OIcon name="close" :size="0.5"/>
						</button>
					</template>
					<template v-else>
						<OIcon set="video" name="video-player-movie" :size="1.5"/>
						<div class="text-xs font-bold text-right line-clamp line-clamp-2" v-text="`Zobacz kolejny: ${nextVideoTitle}`"/>
						<OIcon name="arrow-right" :size="1.25"/>
					</template>
				</div>
			</template>
		</div>
	</div>
</div>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue';
import { mapWritableState } from 'pinia';
import { ToasterElement } from 'ofe-common/components';
import { TutorialVideo, useCustomerOnboardingStore } from '@customer/stores/customerOnboarding';
import jsonFetch from '@/globals/jsonFetch';

/**
 * Komponent wyzwalający odtwarzanie filmu.
 */
export default {
	name: 'VideoTrigger',
	components: {
		VideoPlayer: defineAsyncComponent(() => import('./VideoPlayer.vue')),
		ToasterElement,
	},
	props: {
		/**
		 * Lista filmów do odtworzenia z tytułem i nazwą pliku.
		 */
		videos: {
			type: Array as () => TutorialVideo[],
			required: true,
		},

		/**
		 * Miejsce w którym znajduje się infoboks.
		 */
		source: {
			type: String,
			required: true,
		},

		/**
		 * Nagłówek infoboksa i tytuł filmu.
		 */
		header: {
			type: String,
			default: '',
		},

		/**
		 * Treść infoboksa.
		 */
		body: {
			type: String,
			default: '',
		},

		/**
		 * Czy infoboks ma być w wersji mobilnej (przycisk pod tekstem)?
		 */
		mobileSize: {
			type: Boolean,
			default: false,
		},

		/** Czy ukryć boks? */
		hideTrigger: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			/**
			 * Czy okienko odtwarzacza jest widoczne?
			 */
			isPlayerVisible: false,

			/**
			 * Czy odtwarzacz jest w trybie pełnoekranowym?
			 */
			isFullscreenEnabled: false,

			/**
			 * Referencja do opóźnienia ukrywającego kontrolki odtwarzacza.
			 */
			hideControlsTimeout: 0,

			/**
			 * Czy pokazywać kontrolki odtwarzania?
			 */
			hideControls: false,

			/**
			 * Czy odtworzyć film automatycznie?
			 */
			autoplay: true,

			/**
			 * Indeks aktualnie wyświetlanego filmu.
			 */
			index: 0,

			/**
			 * Czy filmik jest obecnie zatrzymany?
			 */
			isPaused: false,

			/**
			 * Czy filmik został obejrzany do końca?
			 */
			isVideoFinished: false,

			/**
			 * Czy wyłączono pokazywanie linku do kolejnego filmu?
			 */
			isLinkHidden: false,

			rated: 0,
		};
	},
	computed: {
		...mapWritableState(useCustomerOnboardingStore, [
			'globalTutorialVideo',
		]),

		/**
		 * Czy odtwarzacz zajmuje całą powierzchnię ekranu (tj. komórka lub włączony pełny ekran)?
		 */
		isFullscreen() {
			return this.ScreenSize <= 1 || this.isFullscreenEnabled;
		},

		/**
		 * Referencja do elementu odtwarzacza.
		 */
		player() {
			return this.$refs.player as HTMLDivElement;
		},

		basePath() {
			const { origin } = document.location;
			const staticHost = origin.replace('www', 'static'); // origin.includes('dev') ? origin :
			return `${staticHost}/images/videos/customer-app`;
		},

		/**
		 * Ścieżka do pliku wideo.
		 */
		videoFilename() {
			return `${this.basePath}/${this.currentVideoName}.mp4`;
		},

		/**
		 * Alternatywna ścieżka do pliku wideo.
		 */
		oggVideoFilename() {
			return `${this.basePath}/${this.currentVideoName}.ogg`;
		},

		thumbnailFilename() {
			return `${this.basePath}/${this.videos[0].name}-${this.mobileSize ? 'm' : 'd'}.png`;
		},

		currentVideoName() {
			return this.videos[this.index].name;
		},

		nextVideoTitle() {
			return this.videos[(this.index + 1) % this.videos.length].title;
		},

		canShowLink() {
			return !this.isLinkHidden && this.videos[this.index].link;
		},
	},
	mounted() {
		document.addEventListener('fullscreenchange', this.fullscreenEventListener);
		this.resetControlsVisibility();
		this.checkAutoplay();
		if (this.hideTrigger) {
			this.openPlayer(false);
			this.isPlayerVisible = true;
		}
	},
	unmounted() {
		document.removeEventListener('fullscreenchange', this.fullscreenEventListener);
	},
	methods: {
		rate(value: number) {
			this.rated = value;

			// Toster w tym elemencie, ponieważ nie widać tego globalnego jeżeli jesteśmy na pełnym ekranie.
			(this.$refs['toaster'] as typeof ToasterElement).Show({ Message: 'Odpowiedź została zapisana' });

			try {
				jsonFetch('/myoferteo/konto-json/save-video-rate.do', {
					body: {
						name: this.currentVideoName,
						rate: value === 1 ? 'positive' : 'negative',
					},
					method: 'POST',
				});
			} catch (err) {
				// Nie pokazujemy tutaj błędu
			}

			window.setTimeout(() => {
				this.videos[this.index].wasRated = true;
			}, 1000);
		},
		switchVideo(direction: number) {
			if (!this.videos) { return; }
			this.index = (this.index + direction + this.videos.length) % this.videos.length;
			this.sendAnalytics(this.currentVideoName, 0);
			this.isVideoFinished = false;
			this.isLinkHidden = false;
			this.rated = 0;
		},
		goToLink() {
			const { link } = this.videos[this.index];
			if (link) {
				document.location.href = link;
			}
		},
		checkAutoplay() {
			const param = new URLSearchParams(window.location.search).get('autoplay') === 'true';
			if (!param || window.videoAutoplayTriggered) { return; }
			this.autoplay = false;
			this.openPlayer(false);
			window.videoAutoplayTriggered = true;
		},
		/**
		 * Ukrywa pasek z kontrolkami po dwóch sekundach oraz pokazuje go w momencie, kiedy poruszymy myszką.
		 */
		resetControlsVisibility() {
			if (this.hideControlsTimeout) {
				clearTimeout(this.hideControlsTimeout);
			}

			window.setTimeout(() => {
				this.hideControls = false;
			}, 50);

			this.hideControlsTimeout = window.setTimeout(() => {
				this.hideControls = true;
			}, 2000);
		},

		/**
		 * Wyświetla odtwarzacz oraz wysyła informację analityczną o jego otwarciu.
		 */
		openPlayer(mobileFullscreen = true) {
			if (!mobileFullscreen) {
				this.sendAnalytics(this.currentVideoName, 0);
			}

			if (this.ScreenSize > 1 && this.globalTutorialVideo.source !== this.source) {
				const { videos, source } = this;
				this.globalTutorialVideo = { videos, source };
				return;
			}

			this.isPlayerVisible = true;

			// Na mobilce od razu włączamy tryb pełnoekranowy.
			if (mobileFullscreen && this.ScreenSize <= 1) {
				this.onToggleFullscreen();
			}
		},

		/**
		 * Zamyka odtwarzacz oraz wyłącza tryb pełnoekranowy.
		 */
		async closePlayer() {
			try {
				if (document.fullscreenElement) {
					await document.exitFullscreen();
				}
			} catch (err) {
				// Sypie błędem w momencie, kiedy próbujemy wyjść z trybu pełnoekranowego,
				// a nie jesteśmy w nim. Można zignorować.
			}

			if (this.ScreenSize > 1) {
				this.globalTutorialVideo.source = '';
				return;
			}

			this.isPlayerVisible = false;
			this.isFullscreenEnabled = false;
		},

		/**
		 * Wysyła informację analityczną o obejrzeniu kolejnej ćwiartki filmu.
		 */
		onVideoProgress(progress: number) {
			this.isVideoFinished = progress === 100;
			this.sendAnalytics(this.currentVideoName, progress);
		},

		/**
		 * Nasłuchiwanie na zmianę trybu pełnoekranowego.
		 */
		fullscreenEventListener() {
			this.isFullscreenEnabled = !!document.fullscreenElement;
		},

		/**
		 * Włącza lub wyłącza tryb pełnoekranowy.
		 */
		async onToggleFullscreen() {
			const { player } = this;
			if (document.fullscreenElement) {
				try {
					if (document.fullscreenElement) {
						await document.exitFullscreen();
					}
				} catch (err) {
					// Sypie błędem w momencie, kiedy próbujemy wyjść z trybu pełnoekranowego,
					// a nie jesteśmy w nim. Można zignorować.
				}
			} else {
				player.requestFullscreen({
					navigationUI: 'hide',
				});
			}
		},

		/**
		 * Wysyła informację analityczną do GA i bazy danych.
		 */
		sendAnalytics(videoName: string, progress: number) {
			if (progress > 0) {
				this.Analytics.sendGAEvent('videoPlayer_progress', { videoName, progress, videoSource: this.source });
			} else {
				this.Analytics.sendGAEvent('videoPlayer_open', { videoName, videoSource: this.source });
			}

			fetch('/myoferteo/konto-json/log-video-track.do', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name: videoName,
					playbackTime: progress,
					source: this.source,
				}),
			});
		},
	},
};
</script>
